import { Link } from "react-router-dom";
import { MapPin, BadgeCheck, Repeat, Truck, Hand, ThumbsUp, Star, Sparkles, HeartHandshake, WalletCards } from "lucide-react";
import { useMemo } from "react";
import { imgSrc } from "@/lib/api";
import { getCachedCoords, distanceMiles, formatDistance } from "@/lib/geo";
import { CARD_TYPE_LABELS } from "@/lib/cards";

const DELIVERY_LABEL = { local: "Local", shipping: "Ships", both: "Local · Ships" };
const DELIVERY_ICON  = { local: Hand,  shipping: Truck, both: Repeat };

const isFreshlyListed = (created_at) => {
  if (!created_at) return false;
  const hoursOld = (Date.now() - new Date(created_at).getTime()) / (1000 * 60 * 60);
  return hoursOld < 48;
};

/**
 * Marketplace listing card — info-rich, scannable, badge-driven.
 * White surface, sharp 1px border, hover lifts subtly.
 */
export default function ListingCard({ listing, compact = false }) {
  const img = (listing.images && listing.images[0]) || "";
  const wants = (listing.wants || []).slice(0, 2);
  const owner = listing.owner || {};
  const DeliveryIcon = DELIVERY_ICON[listing.delivery] || null;
  const isNew = isFreshlyListed(listing.created_at);
  const isTopRated = (owner.positive_pct || 0) >= 95 && (owner.rating_count || 0) >= 3;

  // Compute distance from cached user coords + listing coords (if both exist)
  const distLabel = useMemo(() => {
    const me = getCachedCoords();
    if (!me || typeof listing.lat !== "number" || typeof listing.lng !== "number") return null;
    const miles = distanceMiles(me, { lat: listing.lat, lng: listing.lng });
    return formatDistance(miles);
  }, [listing.lat, listing.lng]);

  const cd = listing.card_details;
  const cardBadge = cd
    ? (cd.graded && cd.grade ? `${cd.grading_company || ""} ${cd.grade}`.trim() : CARD_TYPE_LABELS[cd.card_type] || "Card")
    : null;

  return (
    <Link
      to={`/listing/${listing.id}`}
      className="vyrti-card group overflow-hidden flex flex-col"
      data-testid={`listing-card-${listing.id}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden">
        {img ? (
          <img
            src={imgSrc(img)}
            alt={listing.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500 ease-out"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-slate-300 text-xs uppercase tracking-wide">No image</div>
        )}

        {/* Top-left badges stack */}
        <div className="absolute top-2 left-2 flex flex-col gap-1.5 items-start">
          {isNew && <span className="badge-new">New</span>}
          {cardBadge && (
            <span className="badge-top !bg-slate-900 flex items-center gap-1" data-testid={`card-badge-${listing.id}`}>
              <WalletCards className="w-2.5 h-2.5" /> {cardBadge}
            </span>
          )}
          {isTopRated && <span className="badge-top flex items-center gap-1"><Star className="w-2.5 h-2.5 fill-current" /> Top trader</span>}
          {listing.status === "under_review" && (
            <span className="badge-deal" data-testid={`pill-review-${listing.id}`}>Under review</span>
          )}
        </div>

        {/* Top-right meta */}
        {DeliveryIcon && (
          <span className="absolute top-2 right-2 bg-white/95 border border-slate-200 px-1.5 py-0.5 rounded text-[10px] font-semibold text-slate-700 flex items-center gap-1" title={`Delivery: ${listing.delivery}`}>
            <DeliveryIcon className="w-3 h-3" /> {DELIVERY_LABEL[listing.delivery]}
          </span>
        )}

        {listing.status === "traded" && (
          <div className="absolute inset-0 bg-slate-900/55 flex items-center justify-center">
            <span className="bg-white text-slate-900 px-3 py-1 rounded text-[11px] font-bold tracking-wider uppercase">Traded</span>
          </div>
        )}
      </div>

      {/* Card body — dense info */}
      <div className="p-3 flex flex-col gap-1.5 flex-1">
        <h3 className="text-[14px] md:text-[15px] font-semibold text-slate-900 leading-snug line-clamp-2 group-hover:text-[#0654BA] transition-colors">
          {listing.title}
        </h3>

        {cd?.game && (
          <p className="text-[11px] font-semibold text-amber-700 line-clamp-1" data-testid={`card-game-${listing.id}`}>
            {cd.game}{cd.set_name ? ` · ${cd.set_name}` : ""}{cd.year ? ` · ${cd.year}` : ""}
          </p>
        )}

        {/* Wants row — the "price" of this listing */}
        {wants.length > 0 && (
          <p className="text-[12px] text-slate-700 leading-snug line-clamp-1">
            <span className="text-slate-400 font-medium">Wants </span>
            <span className="text-[#0654BA] font-medium">{wants.join(" · ")}</span>
            {(listing.wants || []).length > 2 && <span className="text-slate-400"> +{listing.wants.length - 2}</span>}
          </p>
        )}
        {listing.open_to_offers && wants.length === 0 && (
          <p className="text-[12px] text-[#0654BA] font-medium flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Open to offers
          </p>
        )}

        {/* Bottom row — distance/location + owner rating */}
        {!compact && (
          <div className="mt-auto pt-2 flex items-center justify-between gap-2 text-[11px] text-slate-500">
            <span className="flex items-center gap-1 truncate" title={listing.location || "Anywhere"}>
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{distLabel || listing.location || "Anywhere"}</span>
            </span>
            {owner.rating_count > 0 ? (
              <span className="flex items-center gap-0.5 text-emerald-700 font-semibold whitespace-nowrap">
                <ThumbsUp className="w-3 h-3" />
                {Math.round(owner.positive_pct || 0)}%
                <span className="text-slate-400 font-normal">({owner.rating_count})</span>
              </span>
            ) : owner.vouch_count > 0 ? (
              <span className="flex items-center gap-0.5 text-[#0654BA] font-semibold whitespace-nowrap" data-testid="card-vouch-chip" title={`${owner.vouch_count} traders vouch for ${owner.name}`}>
                <HeartHandshake className="w-3 h-3" />
                {owner.vouch_count} {owner.vouch_count === 1 ? "vouch" : "vouches"}
              </span>
            ) : (
              <span className="flex items-center gap-1 text-slate-400">
                {owner.verified && <BadgeCheck className="w-3 h-3 text-[#0654BA]" />}
                {owner.name}
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}
